import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
    type ST2110,
    api,
    central,
    base,
    nodeId,
    ApiError,
    normCfg,
    onUnauthorized,
    type Change,
    type Config,
    type Event,
    type Kind,
    type NDISettings,
    type PTPSettings,
    type Status,
    type CentralUser,
    type NodeInfo,
    type NodePair,
    virtualConfig,
} from './api'
import { getItem, setItem, store, stored } from './storage'
import { streamHealth } from './watchdog'
import { at, inConfig, withNode } from './util'
import { nodeFeatures } from './features'
import { offlineWord } from './reboot'

// Cancelled: the user did not confirm what a change interrupts (planned);
// the form stays as it was
export class Cancelled extends Error {
    constructor() {
        super('cancelled')
    }
}

export type Preset = '10m' | '1h' | '6h' | '24h' | '7d'
export const RANGE_SECONDS: Record<Preset, number> = {
    '10m': 600,
    '1h': 3600,
    '6h': 6 * 3600,
    '24h': 24 * 3600,
    '7d': 7 * 86400,
}
// A window is either a rolling preset (ends now) or an absolute [from, to].
export type Window = { preset: Preset } | { from: number; to: number }

export interface Selection {
    kind: 'input' | 'encode' | 'destination' | 'output' | 'machine' | 'storage'
    id: string
}

export { stored } from './storage'

function loadWindow(): Window {
    const w = stored<Partial<{ preset: string; from: number }> | null>('ese.window', null)
    if (w && (w.preset || w.from)) return w as Window
    return { preset: '24h' }
}

export const useNode = defineStore('node', {
    state: () => ({
        authChecked: false,
        passwordSet: false,
        loggedIn: false,
        // served by centrald: login is central's (email + password), the node API
        // is reached through /nodes/{id}; with no node in the URL show the fleet
        central: false,
        setupRequired: false,
        setupToken: false, // the first superadmin needs the setup token from central's log
        sso: null as { enabled: boolean; label?: string } | null, // central's single sign-on, for the sign-in page
        readOnly: false, // a viewer of this node through central (central roles.go): it is shown, not changed
        // this node as central has it (its version, its features) and each
        // feature's first version central keeps: what a gate says (features.ts)
        info: null as NodeInfo | null,
        firstVersions: null as Record<string, string> | null,
        showUpdate: false, // a gate's Update button opens the node's update (App)
        // its 1+1 pair (central pairs.go) and the partner's name: a change here goes there too
        pair: null as NodePair | null,
        partner: '',
        partnerOnline: true, // is the partner connected to central (a reboot of this node leaves the feed without a side when not)
        user: null as CentralUser | null,
        fleet: false,
        config: null as Config | null,
        status: null as Status | null,
        events: [] as Event[], // rolling tail, live-updated
        windowEvents: [] as Event[], // events fetched for an absolute window
        eventsLoading: true, // the first tail is not here yet: timelines show a placeholder
        windowLoading: false, // an absolute window's events are being fetched
        connected: false,
        connectError: '', // why the node's page cannot load (offline behind central, unreachable); retried
        window: loadWindow(),
        selection: null as Selection | null,
        panelOpen: getItem('ese.panel') !== 'closed',
        collapsed: new Set<string>(stored<string[]>('ese.collapsed', [])),
        // the last thing said (a save, a copy, a failure), kept in the status
        // bar until the next one: nothing pops up and goes away by itself
        toast: '',
        toastBad: false, // the toast reports a failure
        toastAt: 0,
        // a change's plan waiting for the user's yes (ChangesDialog)
        pendingPlan: null as null | { changes: Change[]; resolve: (ok: boolean) => void },
        es: null as EventSource | null,
        reconnecting: false,
        lastStreamAt: 0, // when the stream last brought anything (watchStream)
        skew: 0, // ms the node's clock is ahead of the browser's
        now: Date.now(),
    }),
    getters: {
        // [from, to] in ms for the current window
        span(s): [number, number] {
            if ('preset' in s.window) return [s.now - RANGE_SECONDS[s.window.preset] * 1000, s.now]
            return [s.window.from * 1000, s.window.to * 1000]
        },
        live(s): boolean {
            return 'preset' in s.window
        },
        // when this node's page last heard from its node, on the node's clock,
        // while it is disconnected (0 while connected): its bars stop knowing
        // there, and a banner says since when it is lost. The bars went on
        // green and the dots stayed lit, as if all was well.
        lostAt(s): number {
            if (s.fleet || s.connected || !s.lastStreamAt) return 0
            return s.lastStreamAt + s.skew
        },
        // what the node can: null for every feature (features.ts)
        features(s): string[] | null {
            return nodeFeatures(s.central && !!nodeId && !virtualConfig, s.status?.features, s.info)
        },
        // the window's events by entity ("kind/id"), all and state changes
        // only: rebuilt when the events change, not each second for each bar
        // (every bar filtered all 10,000 events every second: WEB-33).
        // While noded was not running (its stop until its start) every
        // entity's state is unknown, as the fleet's while the node is
        // offline: noded writes no state of its entities when it stops.
        byEntity(s): { all: Map<string, Event[]>; state: Map<string, Event[]> } {
            const all = new Map<string, Event[]>()
            const state = new Map<string, Event[]>()
            const life: Event[] = []
            for (const e of 'preset' in s.window ? s.events : s.windowEvents) {
                const k = e.kind + '/' + e.entity
                const l = all.get(k)
                if (l) l.push(e)
                else all.set(k, [e])
                if (e.kind === 'node' && (e.type === 'stop' || e.type === 'start')) life.push(e)
            }
            for (const [k, l] of all) {
                const marked = k.startsWith('node/') ? l : withNode(life, l)
                all.set(k, marked)
                state.set(
                    k,
                    marked.filter((e) => e.type === 'state'),
                )
            }
            return { all, state }
        },
        openAlarms(s): Event[] {
            if (!s.status) return []
            const bad: Event[] = []
            const latest = new Map<string, Event>()
            for (const e of s.events) if (e.type === 'state') latest.set(e.kind + '/' + e.entity, e)
            for (const e of latest.values())
                if (e.severity !== 'info' && (!s.config || inConfig(s.config, e.kind, e.entity))) bad.push(e)
            return bad
        },
    },
    actions: {
        async init() {
            // any 401 later (a session that expired): back to the login, not a
            // page of stale data
            onUnauthorized(() => {
                if (!this.loggedIn) return
                this.loggedIn = false
                this.connected = false
                this.es?.close()
                this.es = null
            })
            // one /api/auth at the site root tells us who serves the app: central
            // answers with setup_required, a node with password_set. Retried: a
            // noded restarting, or a 502 from the load balancer, left the page on
            // "Connecting…" for good.
            let a: Record<string, unknown>
            for (;;) {
                try {
                    a = (await central.auth()) as unknown as Record<string, unknown>
                    this.connectError = ''
                    break
                } catch (e) {
                    this.connectError = `Cannot reach the server (${e instanceof Error ? e.message : String(e)}).`
                    await new Promise((r) => setTimeout(r, 5000))
                }
            }
            this.central = 'setup_required' in a
            if (this.central) {
                this.setupRequired = a.setup_required as boolean
                this.setupToken = a.setup_token === true
                this.sso = (a.sso as { enabled: boolean; label?: string } | undefined) ?? null
                this.loggedIn = a.logged_in as boolean
                this.user = (a.user as CentralUser) ?? null
                this.fleet = base === ''
            } else {
                this.passwordSet = a.password_set as boolean
                this.loggedIn = a.logged_in as boolean
            }
            this.authChecked = true
            setInterval(() => {
                this.now = Date.now() + this.skew
                this.watchStream()
            }, 1000)
            if (this.loggedIn && !this.fleet) await this.connectOrExplain()
        },
        // connect, or say why not and try again every few seconds: a node that
        // is offline behind central used to leave the page on "Connecting…"
        async connectOrExplain() {
            if (this.reconnecting) return
            this.reconnecting = true
            try {
                await this.connectLoop()
            } finally {
                this.reconnecting = false
            }
        },
        async connectLoop() {
            for (;;) {
                if (!this.loggedIn) return // a 401 on the way: the login shows
                try {
                    await this.connect()
                    this.connectError = ''
                    return
                } catch (e) {
                    let msg = e instanceof Error ? e.message : String(e)
                    if (this.central)
                        try {
                            const n = (await central.nodes()).find((x) => x.id === nodeId)
                            if (!n) msg = 'This node does not exist, or you may not see it.'
                            else if (!n.online)
                                msg = `${n.name} is ${offlineWord(n, this.now)}${n.last_seen ? ` (last seen ${new Date(n.last_seen).toLocaleString()})` : ''}.`
                        } catch {
                            /* keep the first reason */
                        }
                    this.connectError = msg
                    await new Promise((r) => setTimeout(r, 5000))
                }
            }
        },
        async login(password: string, email = '', setupToken = '', totp = '') {
            if (this.central) {
                const r = this.setupRequired
                    ? await central.setup(email, password, setupToken)
                    : await central.login(email, password, totp)
                this.user = r.user
                this.setupRequired = false
            } else {
                await api.login(password)
                this.passwordSet = true
            }
            this.loggedIn = true
            if (!this.fleet) await this.connectOrExplain()
        },
        async logout() {
            await (this.central ? central.logout() : api.logout())
            this.es?.close()
            this.loggedIn = false
            this.connected = false
        },
        async connect() {
            this.loadInfo()
            if (this.central && nodeId && !virtualConfig)
                void central
                    .features()
                    .then((f) => (this.firstVersions = f.first))
                    .catch(() => {})
            const [cfg, st] = await Promise.all([api.config(), api.status()])
            this.config = cfg
            this.status = st
            this.connected = true // it answered: no "disconnected" flash while the stream opens
            this.skewFrom(st)
            this.events = await api.events({ limit: 5000 })
            this.eventsLoading = false
            if (!this.live) void this.loadWindow()
            this.es?.close()
            const es = new EventSource(api.stream())
            // whenever the stream (re)opens, fetch what it missed: events are
            // only pushed while it is open, and the first fetch above ran before
            const catchUp = (since: number) => {
                api.events({ since, limit: 5000 })
                    .then((more) => this.addEvents(more))
                    .catch(() => {})
            }
            // a stream that opens again may be a node that restarted as another
            // version (an update, or back to an older one): central's record of
            // it (its version, whether it is supported, what it can) is read
            // again; the browser reopens a dropped stream by itself, without
            // connect
            let reopened = false
            es.onopen = () => {
                catchUp(this.events.at(-1)?.id ?? 0)
                if (reopened) this.loadInfo()
                reopened = true
            }
            // the node dropped events this page was too slow to take (EVT-1)
            es.addEventListener('gap', (m) => {
                catchUp((JSON.parse(m.data as string) as { since: number }).since)
            })
            this.lastStreamAt = Date.now()
            es.addEventListener('status', (m) => {
                this.lastStreamAt = Date.now()
                const st = JSON.parse(m.data as string) as Status
                this.status = st
                this.skewFrom(st)
                this.connected = true
            })
            es.addEventListener('config', (m) => {
                this.config = normCfg(JSON.parse(m.data as string) as Config)
            })
            es.addEventListener('meters', (m) => {
                this.lastStreamAt = Date.now()
                const peaks = JSON.parse(m.data as string) as Record<string, number[]>
                if (!this.status) return
                for (const [id, db] of Object.entries(peaks))
                    if (this.status.inputs[id] && db) this.status.inputs[id].peak_db = db
            })
            es.addEventListener('event', (m) => {
                this.addEvents([JSON.parse(m.data as string) as Event])
            })
            es.onerror = () => {
                this.connected = false
                // the browser retries a dropped stream by itself, but not one
                // refused (central's 503 while the node is offline, a 401): that
                // one is closed for good, and the page froze until a reload
                if (es.readyState === EventSource.CLOSED && this.es === es) {
                    this.es = null
                    setTimeout(() => this.connectOrExplain(), 5000)
                }
            }
            this.es = es
        },
        // watchStream notices a stream that stopped bringing anything while
        // the browser still holds it open (watchdog.ts): disconnected after 5 s,
        // a new connection after 15 s
        watchStream() {
            const es = this.es
            if (!es) return
            const h = streamHealth(this.lastStreamAt, Date.now())
            if (h === 'ok') return
            this.connected = false
            if (h === 'dead') {
                es.close()
                this.es = null
                void this.connectOrExplain()
            }
        },
        // addEvents merges events in by id (the stream and a catch-up fetch
        // overlap), oldest first, the newest 10,000 kept
        addEvents(more: Event[]) {
            if (!more.length) return
            const seen = new Set(this.events.map((e) => e.id))
            const add = more.filter((e) => !seen.has(e.id))
            if (!add.length) return
            const last = this.events.at(-1)?.id ?? 0
            const all = this.events.concat(add)
            if (add.some((e) => e.id < last)) all.sort((a, b) => a.id - b.id)
            this.events = all.length > 10000 ? all.slice(all.length - 10000) : all
        },
        // the node's clock, not the browser's: a laptop 40 s behind drew the
        // newest 40 s of every bar in the previous state
        skewFrom(st: Status) {
            const t = Date.parse(st.time)
            if (!Number.isNaN(t)) this.skew = t - Date.now()
        },
        setWindow(w: Window) {
            this.window = w
            store('ese.window', w)
            if (!('preset' in w)) void this.loadWindow()
        },
        async loadWindow() {
            if ('preset' in this.window) return
            // one extra hour before the window so the state at its start is known
            this.windowLoading = true
            try {
                this.windowEvents = await api.events({
                    from: this.window.from - 3600,
                    to: this.window.to,
                    limit: 10000,
                })
            } finally {
                this.windowLoading = false
            }
        },
        togglePanel() {
            this.panelOpen = !this.panelOpen
            setItem('ese.panel', this.panelOpen ? 'open' : 'closed')
        },
        toggleCollapse(id: string) {
            const c = new Set(this.collapsed)
            if (!c.delete(id)) c.add(id)
            this.collapsed = c
            store('ese.collapsed', [...c])
        },
        select(sel: Selection | null) {
            this.selection = sel
            if (sel && !this.panelOpen) this.togglePanel()
        },
        // the panel's ✕ hides it, as the menu's "Hide the detail panel"
        // does, and clears the selection; the next pick opens it again
        // (Niels: the ✕ left an empty panel standing)
        closePanel() {
            this.selection = null
            if (this.panelOpen) this.togglePanel()
        },
        // planned writes a change once the user has confirmed what it
        // interrupts, if anything (Niels: settings change live, and what has
        // to restart asks first)
        async planned(plan: () => Promise<Change[] | Config>, write: () => Promise<Config>) {
            const p = await plan()
            if (!Array.isArray(p)) {
                this.config = normCfg(p) // a node from before plans: it applied the change
                return
            }
            if (p.length && !(await new Promise<boolean>((resolve) => (this.pendingPlan = { changes: p, resolve }))))
                throw new Cancelled()
            this.config = await write()
            this.refreshPair()
        },
        // loadInfo reads this node as central has it: the caller's role on it
        // (a viewer sees the page without its editing), its version and
        // features, its pair
        loadInfo() {
            if (!this.central || !nodeId || virtualConfig) return
            void central
                .nodes()
                .then((ns) => {
                    this.info = ns.find((n) => n.id === nodeId) ?? null
                    this.readOnly = this.info?.role === 'viewer'
                    this.pairFrom(ns)
                })
                .catch(() => {})
        },
        // a paired node's pair as central has it now (after a change: did it
        // reach the partner), and once more when the configs have come in
        refreshPair() {
            if (!this.pair) return
            const load = () =>
                void central
                    .nodes()
                    .then((ns) => this.pairFrom(ns))
                    .catch(() => {})
            load()
            setTimeout(load, 2500)
        },
        pairFrom(ns: NodeInfo[]) {
            const p = ns.find((n) => n.id === nodeId)?.pair ?? null
            this.pair = p
            this.partner = (p && ns.find((n) => n.id === p.partner)?.name) || ''
            this.partnerOnline = ns.find((n) => n.id === p?.partner)?.online ?? true
        },
        async save(kind: Kind, id: string, body: unknown) {
            const v = this.config!.version
            try {
                await this.planned(
                    () => api.plan(kind, id, body, v),
                    () => api.put(kind, id, body, v),
                )
            } catch (e) {
                if (!(e instanceof Cancelled)) this.fail(e)
                throw e
            }
        },
        async remove(kind: Kind, id: string, query = '') {
            try {
                this.config = await api.del(kind, id, this.config!.version, query)
                if (this.selection?.id === id) this.selection = null
                this.refreshPair()
            } catch (e) {
                this.fail(e)
                throw e
            }
        },
        // a list's order as dragged (reorder.ts): restarts nothing, so no plan
        async reorder(kind: Kind, ids: string[]) {
            try {
                this.config = await api.order(kind, ids, this.config!.version)
                this.refreshPair()
            } catch (e) {
                this.fail(e)
            }
        },
        // patch changes an entity as it is when the change is written: a
        // toggle or a select built its body before the confirm, so another
        // operator's change arriving meanwhile made the write a 409, or was
        // undone by it (WEB-42). change is applied to the entity as the
        // config holds it at the plan and again at the write.
        async patch<T extends { id: string }>(kind: Kind, id: string, change: (cur: T) => T) {
            const body = () => {
                const list = (this.config as unknown as Record<string, T[] | undefined>)[kind] ?? []
                const cur = list.find((x) => x.id === id)
                if (!cur) throw new Error('It no longer exists: someone else removed it.')
                return change(cur)
            }
            try {
                await this.planned(
                    () => api.plan(kind, id, body(), this.config!.version),
                    () => api.put(kind, id, body(), this.config!.version),
                )
            } catch (e) {
                if (!(e instanceof Cancelled)) this.fail(e)
                throw e
            }
        },
        async saveNode(body: {
            name: string
            site?: string
            tags?: string[]
            max_buffer_seconds?: number
            full_buffers?: boolean
            ndi?: NDISettings
            ptp?: PTPSettings
            st2110?: ST2110
        }) {
            const v = this.config!.version
            try {
                await this.planned(
                    () => api.planNode(body, v),
                    () => api.putNode(body, v),
                )
            } catch (e) {
                if (!(e instanceof Cancelled)) this.fail(e)
                throw e
            }
        },
        // a machine problem acknowledged on the node, or no longer (DECISIONS
        // 2026-10-17): the machine's state stops or starts standing on it
        async ackProblem(key: string, note: string | null) {
            try {
                const v = this.config!.version
                this.config = note === null ? await api.unackProblem(key, v) : await api.ackProblem(key, note.trim(), v)
                this.notify(
                    note === null ? 'Acknowledgement withdrawn: the problem counts again' : 'Problem acknowledged',
                )
            } catch (e) {
                this.fail(e)
                throw e
            }
        },
        // monitoring locked on the node (an editor's; DECISIONS 2026-10-10):
        // locking cuts whoever watches, unlocking lets them watch again
        async setMonitorLock(lock: boolean) {
            try {
                this.config = normCfg(await (lock ? api.lockMonitoring() : api.unlockMonitoring()))
                this.notify(
                    lock ? 'Monitoring locked: nobody watches this node until it is unlocked' : 'Monitoring unlocked',
                )
            } catch (e) {
                this.fail(e)
                throw e
            }
        },
        // the node's updates held, or the hold lifted (updatehold.go)
        async setUpdateHold(hold: { until?: string; note: string } | null) {
            try {
                this.config = normCfg(await (hold ? api.holdUpdates(hold.until, hold.note) : api.liftUpdateHold()))
                this.notify(hold ? 'Updates held on this node' : 'Updates no longer held')
            } catch (e) {
                this.fail(e)
                throw e
            }
        },
        fail(e: unknown) {
            if (e instanceof ApiError && e.status === 409) {
                api.config()
                    .then((c) => {
                        this.config = c
                    })
                    .catch(() => {}) // the connection state shows a node that stopped answering
                this.notify('Someone else changed the configuration. It has been reloaded, try again.', true)
            } else if (e instanceof ApiError && e.status === 401) {
                this.loggedIn = false
            } else {
                this.notify(e instanceof Error ? e.message : String(e), true)
            }
        },
        // copy to the clipboard with a toast; where the browser refuses (plain
        // HTTP, an iframe), the toast shows the text to copy by hand
        async copy(text: string) {
            try {
                await navigator.clipboard.writeText(text)
                this.notify('Copied')
            } catch {
                this.notify(text)
            }
        },
        notify(msg: string, bad = false) {
            this.toast = msg
            this.toastBad = bad
            this.toastAt = Date.now()
        },
        // all state events of an entity relevant to the current window (including
        // the last one before it, so the bar knows the starting state)
        stateEvents(kind: string, id: string): Event[] {
            return this.byEntity.state.get(kind + '/' + id) ?? []
        },
        // all events of an entity held, before the window too: inWindow takes
        // from them the problem already going on when a stretch starts
        entityEvents(kind: string, id: string): Event[] {
            return this.byEntity.all.get(kind + '/' + id) ?? []
        },
        eventsFor(kind: string, id: string): Event[] {
            const [from, to] = this.span
            return this.entityEvents(kind, id).filter((e) => at(e) >= from && at(e) <= to)
        },
    },
})

// A drawer's request wrapper: clears the error, marks it busy, keeps the
// message for the drawer to show and rethrows so the caller stops.
export function useRun() {
    const err = ref('')
    const busy = ref(false)
    async function run<T>(f: () => Promise<T>): Promise<T> {
        err.value = ''
        busy.value = true
        try {
            return await f()
        } catch (e) {
            err.value = e instanceof Error ? e.message : String(e)
            throw e
        } finally {
            busy.value = false
        }
    }
    return { err, busy, run }
}
